import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
	type ApplicationConfig,
	inject,
	provideBrowserGlobalErrorListeners,
	provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import {
	nexpoEventScopeInterceptor,
	provideNexpoApi,
	provideNexpoBoothApi,
	provideNexpoEventScope,
	provideNexpoMapApi,
} from '@soft2code/nexpo-lib/api';
import { nexpoAuthInterceptor, provideNexpoAuth } from '@soft2code/nexpo-lib/core';
import { provideNexpoI18n } from '@soft2code/nexpo-lib/i18n';
import { provideNexpoMockData } from '@soft2code/nexpo-lib/mock';
import { provideNexpoTheme } from '@soft2code/nexpo-lib/theme';
import { provideNexpoStatusMap } from '@soft2code/nexpo-lib/ui';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { InspectedEvent } from './core/focus/inspected-event';
import { OWNER_STATUS_MAP } from './core/status/status-map';

/**
 * Sem service worker, de propósito: console de mesa, rede boa, duas pessoas.
 * Servir versão velha no meio de um atendimento custaria mais que o offline
 * que ninguém tem.
 */
export const appConfig: ApplicationConfig = {
	providers: [
		provideBrowserGlobalErrorListeners(),
		provideZonelessChangeDetection(),
		provideRouter(
			routes,
			withComponentInputBinding(),
			withViewTransitions({ skipInitialTransition: true }),
			withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
		),
		provideHttpClient(withFetch(), withInterceptors([nexpoAuthInterceptor, nexpoEventScopeInterceptor])),

		// A aplicação é onde o console roda (`NEXPO-OWNER`), não o canal da caixa
		// de chamados (`NEXPO-ADMIN`). Ver `features/support/inbox.ts`.
		provideNexpoApi({ baseUrl: environment.apiUrl, application: 'NEXPO-OWNER' }),
		provideNexpoAuth({ loginPath: '/sign-in', homePath: '/support' }),
		provideNexpoTheme({ defaultPreference: 'light', storageKey: 'nexpo_owner_theme' }),
		provideNexpoI18n({ storageKey: 'nexpo_owner_language' }),

		// O console atravessa todas as feiras: o escopo só existe com uma feira em
		// foco. Fora dela a fonte devolve `null` e nenhuma requisição leva o cabeçalho.
		provideNexpoEventScope(() => inject(InspectedEvent).id()),

		// Em memória só o que não tem rota: pedidos de feira, comunicados e o sino.
		...(environment.useMockData ? [provideNexpoMockData({ latencyMs: 240, anchorToday: true })] : []),
		// **Depois** do mock: desenho e estande vêm da API de verdade.
		provideNexpoMapApi(),
		provideNexpoBoothApi(),

		provideNexpoStatusMap(OWNER_STATUS_MAP),
	],
};
